export interface SeoMetadata {
  title: string;
  description: string;
  path?: string;
  image?: string;
  type?: 'website' | 'article' | 'book';
  robots?: string;
  jsonLd?: Record<string, unknown> | null;
}

const ensureMeta = (selector: string, attributes: Record<string, string>) => {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([key, value]) => element?.setAttribute(key, value));
};

const ensureLink = (rel: string, href: string) => {
  let element = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement('link');
    element.rel = rel;
    document.head.appendChild(element);
  }
  element.href = href;
};

export const setSeoMetadata = ({
  title,
  description,
  path = '/',
  image = '/lingbi-favicon-512.png',
  type = 'website',
  robots = 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1',
  jsonLd = null,
}: SeoMetadata) => {
  const canonicalUrl = new URL(path, window.location.origin).toString();
  const imageUrl = new URL(image, window.location.origin).toString();
  document.title = title;
  ensureMeta('meta[name="description"]', { name: 'description', content: description });
  ensureMeta('meta[name="robots"]', { name: 'robots', content: robots });
  ensureMeta('meta[property="og:title"]', { property: 'og:title', content: title });
  ensureMeta('meta[property="og:description"]', { property: 'og:description', content: description });
  ensureMeta('meta[property="og:type"]', { property: 'og:type', content: type });
  ensureMeta('meta[property="og:url"]', { property: 'og:url', content: canonicalUrl });
  ensureMeta('meta[property="og:image"]', { property: 'og:image', content: imageUrl });
  ensureMeta('meta[property="og:locale"]', { property: 'og:locale', content: 'zh_CN' });
  ensureMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
  ensureMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: title });
  ensureMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description });
  ensureMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: imageUrl });
  ensureLink('canonical', canonicalUrl);

  let script = document.head.querySelector<HTMLScriptElement>('script[data-lingbi-seo="json-ld"]');
  if (!jsonLd) {
    script?.remove();
    return;
  }
  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.dataset.lingbiSeo = 'json-ld';
    document.head.appendChild(script);
  }
  script.text = JSON.stringify(jsonLd).replace(/</g, '\\u003c');
};
